import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Droplets, Plus, Minus, Target, TrendingUp, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { API, getLocalDateString } from '../App';
import { toast } from 'sonner';

const WaterTracker = ({ selectedDate }) => {
  const [waterData, setWaterData] = useState({ glasses: 0, goal: 8, progress_percent: 0 });
  const [weeklyData, setWeeklyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGoalEdit, setShowGoalEdit] = useState(false);
  const [newGoal, setNewGoal] = useState(8);
  const [showWeekly, setShowWeekly] = useState(false);

  useEffect(() => {
    loadWaterData();
  }, [selectedDate]);

  const loadWaterData = async () => {
    try {
      const date = selectedDate || getLocalDateString();
      const response = await axios.get(`${API}/nutrition/water/${date}`);
      setWaterData(response.data);
      setNewGoal(response.data.goal);
    } catch (error) {
      console.error('Error loading water data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWeeklyData = async () => {
    try {
      const response = await axios.get(`${API}/nutrition/water/weekly`);
      setWeeklyData(response.data);
      setShowWeekly(true);
    } catch (error) {
      toast.error('Failed to load weekly data');
    }
  };

  const addWater = async (glasses = 1) => {
    try {
      const date = selectedDate || getLocalDateString();
      const response = await axios.post(`${API}/nutrition/water/log`, {
        date: date,
        glasses: glasses
      });
      setWaterData(response.data);
      
      // Celebration if goal reached!
      if (response.data.glasses >= response.data.goal && waterData.glasses < waterData.goal) {
        toast.success('🎉 Daily water goal reached!', {
          description: `You've had ${response.data.glasses} glasses today!`
        });
      } else {
        toast.success(`+${glasses} glass${glasses > 1 ? 'es' : ''} 💧`);
      }
    } catch (error) {
      toast.error('Failed to log water');
    }
  };

  const resetWater = async () => {
    try {
      const date = selectedDate || getLocalDateString();
      await axios.delete(`${API}/nutrition/water/${date}`);
      setWaterData({ ...waterData, glasses: 0, progress_percent: 0 });
      toast.success('Water log reset');
    } catch (error) {
      toast.error('Failed to reset');
    }
  };

  const updateGoal = async () => {
    try {
      await axios.put(`${API}/nutrition/water/goal`, {
        daily_water_goal: newGoal
      });
      setWaterData({ ...waterData, goal: newGoal });
      setShowGoalEdit(false);
      toast.success(`Water goal set to ${newGoal} glasses`);
    } catch (error) {
      toast.error('Failed to update goal');
    }
  };

  // Calculate progress ring
  const progressPercent = Math.min(100, waterData.progress_percent);
  const circumference = 2 * Math.PI * 45; // radius = 45
  const strokeDashoffset = circumference - (progressPercent / 100) * circumference;

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-blue-900/40 to-cyan-900/40 border-blue-500/30 p-4">
        <div className="animate-pulse flex items-center gap-3">
          <div className="w-24 h-24 rounded-full bg-blue-500/20"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-blue-500/20 rounded w-3/4"></div>
            <div className="h-3 bg-blue-500/20 rounded w-1/2"></div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-blue-900/40 to-cyan-900/40 border-blue-500/30 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Droplets className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-bold text-white">Water Intake</h3>
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={loadWeeklyData}
            className="text-blue-400 hover:text-blue-300 p-1"
          >
            <TrendingUp className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowGoalEdit(!showGoalEdit)}
            className="text-blue-400 hover:text-blue-300 p-1"
          >
            <Target className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Goal Edit */}
      {showGoalEdit && (
        <div className="mb-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
          <p className="text-sm text-blue-300 mb-2">Daily Water Goal (glasses)</p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setNewGoal(Math.max(1, newGoal - 1))}
              className="border-blue-500/50 text-blue-400"
            >
              <Minus className="w-4 h-4" />
            </Button>
            <span className="text-xl font-bold text-white w-12 text-center">{newGoal}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setNewGoal(newGoal + 1)}
              className="border-blue-500/50 text-blue-400"
            >
              <Plus className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              onClick={updateGoal}
              className="ml-2 bg-blue-600 hover:bg-blue-700 text-white"
            >
              Save
            </Button>
          </div>
        </div>
      )}

      {/* Main Content - Progress Ring + Controls */}
      <div className="flex items-center gap-4">
        {/* Progress Ring */}
        <div className="relative w-28 h-28 flex-shrink-0">
          <svg className="w-full h-full transform -rotate-90">
            {/* Background circle */}
            <circle
              cx="56"
              cy="56"
              r="45"
              stroke="rgba(59, 130, 246, 0.2)"
              strokeWidth="8"
              fill="none"
            />
            {/* Progress circle */}
            <circle
              cx="56"
              cy="56"
              r="45"
              stroke={progressPercent >= 100 ? '#22c55e' : '#3b82f6'}
              strokeWidth="8"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-500"
            />
          </svg>
          {/* Center content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Droplets className={`w-6 h-6 ${progressPercent >= 100 ? 'text-green-400' : 'text-blue-400'}`} />
            <span className="text-2xl font-bold text-white">{waterData.glasses}</span>
            <span className="text-xs text-stone-400">of {waterData.goal}</span>
          </div>
        </div>

        {/* Quick Add Buttons */}
        <div className="flex-1">
          <p className="text-sm text-stone-400 mb-2">Quick Add</p>
          <div className="grid grid-cols-3 gap-2">
            <Button
              onClick={() => addWater(1)}
              className="bg-blue-600/80 hover:bg-blue-600 text-white py-4 flex flex-col items-center"
            >
              <span className="text-lg">+1</span>
              <span className="text-xs opacity-70">glass</span>
            </Button>
            <Button
              onClick={() => addWater(2)}
              className="bg-blue-700/80 hover:bg-blue-700 text-white py-4 flex flex-col items-center"
            >
              <span className="text-lg">+2</span>
              <span className="text-xs opacity-70">glasses</span>
            </Button>
            <Button
              onClick={() => addWater(4)}
              className="bg-blue-800/80 hover:bg-blue-800 text-white py-4 flex flex-col items-center"
            >
              <span className="text-lg">+4</span>
              <span className="text-xs opacity-70">glasses</span>
            </Button>
          </div>
          
          {/* Reset button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={resetWater}
            className="mt-2 text-stone-500 hover:text-red-400 text-xs w-full"
          >
            <RotateCcw className="w-3 h-3 mr-1" />
            Reset Today
          </Button>
        </div>
      </div>

      {/* Progress Bar (alternative view) */}
      <div className="mt-4">
        <div className="flex justify-between text-xs text-stone-400 mb-1">
          <span>{Math.round(progressPercent)}% of daily goal</span>
          <span>{waterData.glasses * 250}ml</span>
        </div>
        <div className="h-2 bg-blue-900/50 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              progressPercent >= 100 ? 'bg-green-500' : 'bg-gradient-to-r from-blue-600 to-cyan-500'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Weekly Stats Modal */}
      {showWeekly && weeklyData && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowWeekly(false)}>
          <Card className="bg-stone-900 border-blue-500/30 p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-400" />
                Weekly Water Stats
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowWeekly(false)} className="text-stone-400">
                ✕
              </Button>
            </div>

            {/* Weekly Summary */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-blue-500/10 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-400">{weeklyData.total_glasses}</p>
                <p className="text-xs text-stone-400">Total Glasses</p>
              </div>
              <div className="bg-cyan-500/10 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-cyan-400">{weeklyData.avg_glasses}</p>
                <p className="text-xs text-stone-400">Daily Avg</p>
              </div>
              <div className="bg-green-500/10 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-400">{weeklyData.days_hit_goal}/7</p>
                <p className="text-xs text-stone-400">Goals Hit</p>
              </div>
            </div>

            {/* Daily Breakdown */}
            <div className="space-y-2">
              {weeklyData.days.map((day, index) => {
                const dayName = new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' });
                const percent = Math.min(100, (day.glasses / day.goal) * 100);
                return (
                  <div key={day.date} className="flex items-center gap-3">
                    <span className="w-10 text-xs text-stone-400">{dayName}</span>
                    <div className="flex-1 h-4 bg-blue-900/30 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${day.hit_goal ? 'bg-green-500' : 'bg-blue-500'}`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <span className="w-8 text-xs text-stone-400 text-right">{day.glasses}</span>
                    {day.hit_goal && <span className="text-green-400">✓</span>}
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}
    </Card>
  );
};

export default WaterTracker;
